import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BackLink from '../components/BackLink';
import PageLayout from '../components/ui/PageLayout';
import Alert from '../components/ui/Alert';
import Panel from '../components/ui/Panel';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { buttonClass } from '../components/ui/Button';
import { useApi } from '../apiHelpers';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';

const GhostFlagsAdmin = () => {
    const api = useApi();
    const [loading, setLoading] = useState(true);
    const [clearing, setClearing] = useState(false);
    const [ghostData, setGhostData] = useState(null);
    const [orphanedData, setOrphanedData] = useState(null);
    const [error, setError] = useState(null);
    const [lastAction, setLastAction] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [ghost, orphaned] = await Promise.all([
                api.get('/flags/ghost'),
                api.get('/flags/orphaned')
            ]);
            setGhostData(ghost);
            setOrphanedData(orphaned);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Ghost flag actions
    const handleClearAllGhosts = async () => {
        if (!window.confirm(`Clear all ${ghostData.total_ghost_flags} ghost flags?`)) return;
        setClearing(true);
        try {
            const result = await api.post('/flags/ghost/clear');
            setLastAction({ type: 'success', message: `Cleared ${result.cleared_count} ghost flag(s)` });
            await fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setClearing(false);
        }
    };

    const handleClearDroneGhost = async (droneSerial) => {
        setClearing(true);
        try {
            const result = await api.post(`/flags/ghost/clear/${droneSerial}`);
            setLastAction({ type: 'success', message: `Cleared ${result.cleared_count} flag(s) for ${droneSerial}` });
            await fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setClearing(false);
        }
    };

    // Orphaned status actions
    const handleClearAllOrphaned = async () => {
        if (!window.confirm(`Set ${orphanedData.total_orphaned} drone(s) to RTF?`)) return;
        setClearing(true);
        try {
            const result = await api.post('/flags/orphaned/clear');
            setLastAction({ type: 'success', message: `Set ${result.cleared_count} drone(s) to RTF` });
            await fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setClearing(false);
        }
    };

    const handleClearDroneOrphaned = async (droneSerial) => {
        setClearing(true);
        try {
            await api.post(`/flags/orphaned/clear/${droneSerial}`);
            setLastAction({ type: 'success', message: `Set ${droneSerial} to RTF` });
            await fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setClearing(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const totalIssues = (ghostData?.total_ghost_flags || 0) + (orphanedData?.total_orphaned || 0);

    return (
        <PageLayout kind="workspace" className="text-on-surface pt-6">
            <div>
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <BackLink to="/admin" label="Admin" className="mb-2" />
                        <h1 className="text-3xl font-bold">Flag Sync Detector</h1>
                        <p className="text-on-surface-variant mt-1">
                            Find and fix mismatches between drone conditions and flag statuses
                        </p>
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className={buttonClass('tonal', 'md')}
                    >
                        {loading ? 'Scanning...' : 'Rescan'}
                    </button>
                </div>

                {/* Error */}
                <Reveal show={!!error}>
                    <Alert tone="error" className="mb-6">{error}</Alert>
                </Reveal>

                {/* Success Message */}
                <Reveal show={!!lastAction}>
                    <Alert tone="success" className="mb-6">{lastAction?.message}</Alert>
                </Reveal>

                {/* Loading */}
                {loading && (
                    <SkeletonGroup label="Scanning for sync issues..." className="space-y-4">
                        <Skeleton className="h-32 w-full rounded-panel" />
                        <Skeleton className="h-32 w-full rounded-panel" />
                    </SkeletonGroup>
                )}

                {/* Results */}
                {!loading && ghostData && orphanedData && (
                    <FadeIn>
                        {/* All Clear */}
                        <Reveal show={totalIssues === 0}>
                            <EmptyState variant="panel" title="All Synced!" className="mb-8">
                                No flag/status mismatches detected.
                            </EmptyState>
                        </Reveal>

                        {/* Ghost Flags Section */}
                        <div className="mb-8">
                            <Panel
                                title={<span className="flex items-center gap-2"><span className="text-amber-400">👻</span> Ghost Flags</span>}
                                actions={
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <div className="text-2xl font-bold text-amber-400">{ghostData.total_ghost_flags}</div>
                                            <div className="text-xs text-on-surface-variant">{ghostData.affected_drones} drone(s)</div>
                                        </div>
                                        <Appear show={ghostData.total_ghost_flags > 0}>
                                            <button
                                                onClick={handleClearAllGhosts}
                                                disabled={clearing}
                                                className={buttonClass('warn', 'md')}
                                            >
                                                Clear All
                                            </button>
                                        </Appear>
                                    </div>
                                }
                            >
                                <p className="text-on-surface-variant text-sm">
                                    Active flags on drones marked RTF (Ready To Fly)
                                </p>

                                {ghostData.drones && ghostData.drones.length > 0 && (
                                    <div className="space-y-3 mt-4">
                                        {ghostData.drones.map((drone) => (
                                            <Panel
                                                key={drone.drone_serial}
                                                label={`Ghost flags on ${drone.drone_serial}`}
                                                on="panel"
                                                padding="sm"
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-3">
                                                        <Link
                                                            to={`/drone/${drone.drone_serial}`}
                                                            className="text-primary font-mono font-bold hover:underline"
                                                        >
                                                            {drone.drone_serial}
                                                        </Link>
                                                        <span className="px-2 py-0.5 bg-green-900/50 text-green-300 text-xs rounded">
                                                            {drone.drone_condition}
                                                        </span>
                                                        <span className="text-on-surface-variant text-sm">
                                                            {drone.flags.length} active flag(s)
                                                        </span>
                                                    </div>
                                                    <button
                                                        onClick={() => handleClearDroneGhost(drone.drone_serial)}
                                                        disabled={clearing}
                                                        className={buttonClass('quiet', 'sm')}
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                                <div className="text-xs text-on-surface-variant space-y-1 pl-2 border-l border-gray-700">
                                                    {drone.flags.map((flag) => (
                                                        <div key={flag.flag_id}>
                                                            <span className="text-on-surface-variant">#{flag.flag_id}</span> {flag.descript}
                                                            <span className="text-on-surface-disabled ml-2">({formatDate(flag.dateflagged)})</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Panel>
                                        ))}
                                    </div>
                                )}
                            </Panel>
                        </div>

                        {/* Orphaned Statuses Section */}
                        <div>
                            <Panel
                                title={<span className="flex items-center gap-2"><span className="text-red-400">🚫</span> Orphaned Statuses</span>}
                                actions={
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <div className="text-2xl font-bold text-red-400">{orphanedData.total_orphaned}</div>
                                            <div className="text-xs text-on-surface-variant">drone(s)</div>
                                        </div>
                                        <Appear show={orphanedData.total_orphaned > 0}>
                                            <button
                                                onClick={handleClearAllOrphaned}
                                                disabled={clearing}
                                                className={buttonClass('danger', 'md')}
                                            >
                                                Set All to RTF
                                            </button>
                                        </Appear>
                                    </div>
                                }
                            >
                                <p className="text-on-surface-variant text-sm">
                                    Drones with Flag/OOC status but no active flags
                                </p>

                                {orphanedData.drones && orphanedData.drones.length > 0 && (
                                    <div className="space-y-4 mt-4">
                                        {Object.entries(orphanedData.by_condition || {}).map(([condition, drones]) => (
                                            <div key={condition}>
                                                <div className="text-sm font-medium text-on-surface-variant mb-2">
                                                    {condition} ({drones.length})
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                                    {drones.map((drone) => (
                                                        <Panel
                                                            key={drone.serialnumber}
                                                            label={`Orphaned status on ${drone.serialnumber}`}
                                                            on="panel"
                                                            padding="sm"
                                                        >
                                                          <div className="flex items-center justify-between">
                                                            <Link
                                                                to={`/drone/${drone.serialnumber}`}
                                                                className="text-primary font-mono text-sm hover:underline"
                                                            >
                                                                {drone.serialnumber}
                                                            </Link>
                                                            <button
                                                                onClick={() => handleClearDroneOrphaned(drone.serialnumber)}
                                                                disabled={clearing}
                                                                className={buttonClass('danger-quiet', 'sm')}
                                                            >
                                                                → RTF
                                                            </button>
                                                          </div>
                                                        </Panel>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Panel>
                        </div>
                    </FadeIn>
                )}
            </div>
        </PageLayout>
    );
};

export default GhostFlagsAdmin;
